import { Overlay } from "@angular/cdk/overlay";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { FileConfigModel } from "../_model/form/configs/file-config.model";
import { ImageConfigModel } from "../_model/form/configs/image-config.model";
import { MediaModel } from "../_model/media/media.model";
import { MediaUploadStatus } from "../_model/media/upload-status.interface";
import { UploadDialog, UploadDialogConfig } from "../_shared/form/input/file/dialog/upload.dialog";
import { HTTP, HttpService } from "./http.service";
import { UserService } from "./user.service";
import { HeicToService } from "./heic-to.service";
import { LocalStorage } from "./local-storage.service";
import { isHeicOrHeifImage, truncateFileName } from "../_helper/file.helper";
import { CompressionService } from "./compression.service";

@Injectable({
  providedIn: 'root'
})
export class FileService extends HttpService {

  private url = environment.apiUrl + 'media';

  private fileChange$: {
    [resource: string]: {
      file: Subject<void>,
      image: Subject<void>,
    }
  } = {};

  private cache: {
    [resource: string]: {
      [key: string]: {
        file: {
          config?: FileConfigModel,
          data: MediaModel[],
          deleted: MediaModel[]
        },
        image: {
          config?: ImageConfigModel,
          data: MediaModel[],
          deleted: MediaModel[]
        }
      }
    }
  } = {};

  private queue: {
    [resource: string]: {
      [key: string]: {
        file: FileQueueItem[],
        image: FileQueueItem[]
      }
    }
  } = {};

  private status: {
    [resource: string]: {
      [key: string]: {
        processing: boolean,
        all$: Subject<void>,
        errors: string[],
        file: MediaUploadStatus,
        image: MediaUploadStatus,
        file$: Subject<void>,
        image$: Subject<void>
      }
    }
  } = {};

  //Dialog
  private messageType$: BehaviorSubject<FileOperation> = null;
  private totalFiles$: BehaviorSubject<number> = null;
  private currentFile$: BehaviorSubject<number> = null;
  private uploadDialog: MatDialogRef<UploadDialog> = null;

  constructor(
    protected http: HttpClient,
    protected userService: UserService,
    protected router: Router,
    protected dialog: MatDialog,
    protected localStorage: LocalStorage,
    private overlay: Overlay,
    private compressionService: CompressionService,
    private heicToService: HeicToService
  ) {
    super(http, userService, router, dialog, localStorage);
  }

  public clear() {
    //cache
    this.cache = {};
    //queues
    this.queue = {};
    //status
    for (let resource in this.status) {
      for (let key in this.status[resource]) {
        if (this.status[resource][key].all$ && !this.status[resource][key].all$.closed) {
          this.status[resource][key].all$.next();
          this.status[resource][key].all$.complete();
        }
        this.status[resource][key].all$ = null;
        if (this.status[resource][key].file$ && !this.status[resource][key].file$.closed) {
          this.status[resource][key].file$.next();
          this.status[resource][key].file$.complete();
        }
        this.status[resource][key].file$ = null;
        if (this.status[resource][key].image$ && !this.status[resource][key].image$.closed) {
          this.status[resource][key].image$.next();
          this.status[resource][key].image$.complete();
        }
        this.status[resource][key].image$ = null;
      }
    }
    this.status = {};
    //dialogs
    this.uploadDialog && this.uploadDialog.getState() === 0 && this.uploadDialog.close(); //close if open
    this.uploadDialog = null;
    //observables
    this.messageType$ && !this.messageType$.closed && this.messageType$.complete();
    this.messageType$ = null;
    this.totalFiles$ && !this.totalFiles$.closed && this.totalFiles$.complete();
    this.totalFiles$ = null;
    this.currentFile$ && !this.currentFile$.closed && this.currentFile$.complete();
    this.currentFile$ = null;
  }

  public moveNewQueue(resource: string, key: string) {
    this.createQueues(resource, key);
    if (this.queue[resource]['new']) {
      this.queue[resource][key] = { ...this.queue[resource]['new'] };
      this.queue[resource][key].file = [ ...this.queue[resource]['new'].file ];
      this.queue[resource][key].image = [ ...this.queue[resource]['new'].image ];
      this.clearSpecific(resource, 'new');
    }
  }

  private buildMediaParams(resource: string, key: string, type: 'file' | 'image', fileKey?: string, statuses?: string[], listingKey?: string) {
    return {
      resource_name: resource,
      resource_record_key: key,
      media_category: type === 'file' ? 'document' : 'photo',
      media_key: fileKey || null,
      statuses: statuses?.join() || null,
      property_key: listingKey || null,
    };
  }

  /**
   * Bumps the timestamps on the media for a property. Mostly used for when external system's syncing fails to take hold
   */
  public touch(key: string): Observable<void> {
    return this.PUT(this.url + '/touch/' + key, '');
  }

  /**
   * Imports all of the media for the specified resource, key, type to the listing with the specified listing key
   */
  public import(resource: string, key: string, type: 'file' | 'image', listingKey: string): Observable<void> {
    return this.GET(this.url + '/clone', this.buildMediaParams(resource, key, type, null, null, listingKey));
  }

  /**
   * Gets files for a listing of whichever type passed in
   */
  public getFiles(resource: string, key: string, type: 'file' | 'image'): Observable<MediaModel[]> {
    this.createQueues(resource, key);
    //if we're currently processing a queue, return that so they can see it process
    if (this.status[resource][key][type].processing) return of(this.cache[resource][key][type].data);
    //otherwise, always fetch from the database
    return this.GET(this.url, this.buildMediaParams(resource, key, type)).pipe(map(_files => {
      _files && _files.length && this.validateModel('MediaModel (' + type + ')', _files[0], new MediaModel(), true, false, [ 'modificationTimestamp' ]);
      //we want to allow the array to be shared by both the component and the service to make managing it easier
      this.cache[resource][key][type].data = _files.map(_file => new MediaModel(_file)).sort((a, b) => a.order === b.order ? 0 : a.order > b.order ? 1 : -1);
      return this.cache[resource][key][type].data;
    }));
  }

  public getDeletedFiles(resource: string, key: string, type: 'file' | 'image'): Observable<MediaModel[]> {
    this.createQueues(resource, key);
    if (this.cache[resource][key][type].deleted.length) return of(this.cache[resource][key][type].deleted);
    //fetch from the db i guess
    return this.GET(this.url, this.buildMediaParams(resource, key, type, null, ['Deleted'])).pipe(map(_files => {
      _files && _files.length && this.validateModel('MediaModel (' + type + ')', _files[0], new MediaModel(), true, false, [ 'modificationTimestamp' ]);
      //we want to allow the array to be shared by both the component and the service to make managing it easier
      this.cache[resource][key][type].deleted = _files.map(_file => new MediaModel(_file)).sort((a, b) => a.order === b.order ? 0 : a.order > b.order ? 1 : -1);
      return this.cache[resource][key][type].deleted;
    }));
  }

  /**
   * Deletes a specific file from a specific listing
   */
  private deleteFile(resource: string, key: string, type: 'file' | 'image', file: MediaModel): Promise<void> {
    if (!file.mediaKey) return Promise.resolve(); //can't delete what doesn't exist
    return this.DELETE(this.url, this.buildMediaParams(resource, key, type, file.mediaKey)).pipe(
      map(() => { file.deleted = true })
    ).toPromise();
  }

  /**
   * Restores a specific file from the graveyard.
   */
  private restoreFile(resource: string, key: string, type: 'file' | 'image', file: MediaModel): Promise<void> {
    if (!file.mediaKey) return Promise.resolve(); //can't restore what doesn't exist
    //TODO: new endpoint w/ resource
    if (resource.toLowerCase() !== 'property') {
      environment.warn('[file.service.ts > restoreFile()]: Cannot restore file for resource', resource);
      return Promise.resolve();
    }
    return this.PATCH(this.url + '/listings/' + key + '/' + (type === 'file' ? 'documents' : 'photos') + '/' + file.mediaKey, {})
      .toPromise();
  }

  /**
   * Upload's a specific file's model to the server
   */
  private uploadModel(resource: string, key: string, type: 'file' | 'image', file: MediaModel): Promise<void> {
    file.uploading = true;
    if (!file.mediaKey) return Promise.resolve(); //can't update what doesn't exist
    return this.PUT(this.url, file.noUIModel(), this.buildMediaParams(resource, key, type, file.mediaKey)).pipe(
      map(_file => file.overwrite(new MediaModel(Object.assign(_file, { uploaded: true, uploading: false, processing: false }))))
    ).toPromise();
  }

  /**
   * Uploads a file and it's model as a multiparm/form-data
   */
  private async uploadFile(resource: string, key: string, type: 'file' | 'image', file: MediaModel): Promise<void> {
    file.uploading = true;
    this.fileChange$[resource][type].next();
    if (type === 'image') {
      await this._heicToImage(resource, key, file, this.cache[resource][key][type].config);
      await this._compressImage(resource, key, file, this.cache[resource][key][type].config);
    }
    return this._uploadFile(resource, key, type, file);
  }

  private _uploadFile(resource: string, key: string, type: 'file' | 'image', file: MediaModel): Promise<void> {
    let headers = this.getHeaders(HTTP.POST).delete('Content-Type');
    return this.POST(this.url, file.toFormData(), this.buildMediaParams(resource, key, type), headers).pipe(
      map(_file => {
        file.overwrite(new MediaModel(Object.assign(_file, { uploaded: true, uploading: false, processing: false })))
        if (type === 'image') {
          if (file.unsafeUrl) window.URL.revokeObjectURL(file.unsafeUrl);
          file.unsafeUrl = null;
        }
      })
    ).toPromise();
  }

  private async _heicToImage(_resource: string, _key: string, image: MediaModel, config: ImageConfigModel): Promise<void> {
    //convert heic/heif to png (or w/e)
    if (image.file && isHeicOrHeifImage(image.file)) {
      return this.heicToService.convert(image.file, 1).then(blob => {
        if (blob) {
          let oldName = image.file.name;
          let newName = image.file.name.replace(/.hei(c|f)/i, '.jpg');
          image.file = new File([blob], newName, { type: blob.type });
          image.originalMediaName = truncateFileName(newName, config.maxFileName);
          this.heicToService.clearFile(oldName);
        }
        return Promise.resolve();
      });
    }
    return Promise.resolve();
  }

  private async _compressImage(_resource: string, _key: string, image: MediaModel, config: ImageConfigModel): Promise<void> {
    let compress = false;
    if (!image.compressed) compress = config?.compression?.doCompress(image.file);
    let convert = false;
    if (!image.converted) config?.compression?.doConvert(image.file);
    if (compress || convert) {
      let oldName = image.file.name;
      let newImage = await this.compressionService.compress(image.file, {
        quality: compress ? config.compression.quality : 1,
        maxWidth: config.compression.maxWidth,
        maxHeight: config.compression.maxHeight,
      });
      image.originalMediaName = truncateFileName(newImage.name, config.maxFileName);
      image.file = newImage;
      this.compressionService.clearFile(oldName);
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Replaces the file on the server with a new one.
   */
  private async replaceFile(resource: string, key: string, type: 'file' | 'image', file: MediaModel, config?: FileConfigModel | ImageConfigModel): Promise<void> {
    file.uploading = true;
    this.fileChange$[resource][type].next();
    if (type === 'image') {
      await this._heicToImage(resource, key, file, this.cache[resource][key][type].config || <ImageConfigModel>config);
      await this._compressImage(resource, key, file, this.cache[resource][key][type].config || <ImageConfigModel>config);
    }
    return this._replaceFile(resource, key, type, file);
  }

  private _replaceFile(resource: string, key: string, type: 'file' | 'image', file: MediaModel): Promise<void> {
    if (!file.mediaKey) return Promise.resolve(); //can't replace what doesn't exist
    let headers = this.getHeaders(HTTP.POST).delete('Content-Type');
    return this.POST(this.url + '/replace', file.toFormData(), this.buildMediaParams(resource, key, type, file.mediaKey), headers).pipe(
      map(_file => {
        file.overwrite(new MediaModel(Object.assign(_file, { uploaded: true, uploading: false, processing: false })))
        if (type === 'image') {
          if (file.unsafeUrl) window.URL.revokeObjectURL(file.unsafeUrl);
          file.unsafeUrl = null;
        }
      })
    ).toPromise();
  }

  /**
   * Reorders the files on the server in accordance with whatever is passed in
   */
  private reorderFiles(resource: string, key: string, type: 'file' | 'image', files: MediaModel[]): Promise<void> {
    let orderings = files.filter(file => file.mediaKey).map(file => { return { mediaKey: file.mediaKey, order: file.order }});
    return this.PUT(this.url + '/reorder', orderings, this.buildMediaParams(resource, key, type)).toPromise();
  }

  /**
   * Downloads a file Blob from the server, using whatever url is passed in
   */
  public downloadFile(url): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' }).pipe( catchError(err => this.error(err)) );
  }

  /**********************************************************************************/
  /* Queueing and management thereof                                                */
  /**********************************************************************************/
  /**
   * Clears the queue for anything that is not presently being uploaded or has errors on it
   */
  public clearUnusedQueues() {
    for (let resource in this.status) {
      for (let key in this.status[resource]) {
        if (
          !(this.status[resource][key].processing || this.status[resource][key].file.processing || this.status[resource][key].image.processing) &&
          !(this.status[resource][key].errors.length || this.status[resource][key].file.errors.length || this.status[resource][key].image.errors.length)
        ) {
          delete this.status[resource][key];
          delete this.queue[resource][key];
        }
      }
      if (!Object.keys(this.status[resource]).length) delete this.status[resource];
      if (!Object.keys(this.queue[resource]).length) delete this.queue[resource];
    }
  }

  /**
   * Clears a specific listing's file queues so long as it's not being uploaded at present
   */
  public clearSpecific(resource: string, key: string) {
    this.createQueues(resource, key);
    if (!(this.status[resource][key].processing || this.status[resource][key].file.processing || this.status[resource][key].image.processing)) {
      delete this.cache[resource][key];
      delete this.status[resource][key];
      delete this.queue[resource][key];
    }
    if (!(Object.keys(this.cache[resource]).length || Object.keys(this.status[resource]).length || Object.keys(this.queue[resource]).length)) {
      delete this.cache[resource];
      delete this.status[resource];
      delete this.queue[resource];
    }
  }

  /**
   * Creates the queue structure for a specific listing's files if they do not already exist
   */
  private createQueues(resource: string, key?: string) {
    if (!key) key = 'draft';
    //build change observables
    if (!this.fileChange$[resource]) {
      this.fileChange$[resource] = {
        file: new Subject(),
        image: new Subject(),
      };
    }
    //build cache
    if (!this.cache[resource]) this.cache[resource] = {};
    if (key && !this.cache[resource][key]) {
      this.cache[resource][key] = {
        file: { config: null, data: [], deleted: [] },
        image: { config: null, data: [], deleted: [] }
      };
    }
    //build queue
    if (!this.queue[resource]) this.queue[resource] = {};
    if (key && !this.queue[resource][key]) {
      this.queue[resource][key] = {
        file: [],
        image: []
      };
    }
    //build status
    if (!this.status[resource]) this.status[resource] = {};
    if (key && !this.status[resource][key]) {
      this.status[resource][key] = {
        processing: false,
        all$: new Subject<void>(),
        errors: [],
        file: { processing: false, errors: [] },
        image: { processing: false, errors: [] },
        file$: new Subject<void>(),
        image$: new Subject<void>()
      }
    }
  }

  /**
   * Returns the appropriate observable that let's the respective components know when to update the view in response to changes in data
   */
  public fileChange(resource: string, type: 'file' | 'image'): Observable<void> {
    this.createQueues(resource);
    return this.fileChange$[resource][type].asObservable();
  }

  /**
   * Queues up a delete operation for a file.
   * Will be executed whenever saveQueue/s() is called.
   */
  public queueRestore(resource: string, key: string, type: 'file' | 'image', files: MediaModel[]) {
    this.createQueues(resource, key);
    for (let file of files) {
      //remove from delete queue
      let index = this.queue[resource][key][type].findIndex(item => item.operation === 'delete' && item.data === file);
      if (index !== -1) {
        //just remove
        this.queue[resource][key][type].splice(index, 1);
      } else {
        //queue a restore operation if not already
        let found = this.queue[resource][key][type].find(item => item.operation === 'restore' && item.data === file);
        if (!found) this.queue[resource][key][type].push({ operation: 'restore', data: file, processed: false });
      }
      //move from deleted back to regular cache, if present
      let jndex = this.cache[resource][key][type].deleted.findIndex(f => f === file);
      if (jndex !== -1) this.cache[resource][key][type].data.push( ...this.cache[resource][key][type].deleted.splice(jndex, 1) );
    }
    this.fileChange$[resource][type].next();
  }

  /**
   * Queues up a delete operation for a file.
   * Will be executed whenever saveQueue/s() is called.
   */
  public queueDelete(resource: string, key: string, type: 'file' | 'image', file: MediaModel) {
    this.createQueues(resource, key);
    //remove from the file queue entirely
    let index = this.queue[resource][key][type].findIndex(item => item.data === file);
    //only remove if not uploading, otherwise delete after-the-fact
    if (index !== -1 && !file.uploading) this.queue[resource][key][type].splice(index, 1);
    //only bother queueing a delete if it's already uploaded or is uploading
    if (file.mediaKey || file.uploading) {
      let found = this.queue[resource][key][type].find(item => item.operation === 'delete' && item.data === file);
      if (!found) this.queue[resource][key][type].push({ operation: 'delete', data: file, processed: false });
    }
    //if heic/f, make sure to clear the converted file from memory
    if (type === 'image' && file.file) {
      this.heicToService.clearFile(file.file.name);
      this.compressionService.clearFile(file.file.name);
    }
    //move from regular cache to deleted cache
    this.cache[resource][key][type].deleted.push(file);
    this.fileChange$[resource][type].next();
  }

  /**
   * Queues up a model (metadata) change operation for a file.
   * Will be executed whenever saveQueue/s() is called.
   */
  public queueModel(resource: string, key: string, type: 'file' | 'image', file: MediaModel) {
    this.createQueues(resource, key);
    //if it's an existing file or we're currently uploading, queue it up
    //otherwise, it's a new file upload and will be in that queue
    if (file.mediaKey || file.uploading) {
      let found = this.queue[resource][key][type].find(item => (item.operation === 'model' || item.operation === 'delete') && item.data === file);
      if (!found) this.queue[resource][key][type].push({ operation: 'model', data: file, processed: false });
    }
  }

  /**
   * Queues up all files that need to be uploaded
   * Will be executed whenever saveQueue/s() is called.
   */
  public queueFiles(resource: string, key: string, type: 'file' | 'image', files: MediaModel[], config: FileConfigModel | ImageConfigModel) {
    this.createQueues(resource, key);
    //make sure we have the config for the file upload
    this.cache[resource][key][type].config = config;
    for (let file of files) {
      let index = this.queue[resource][key][type].findIndex(item => item.data === file);
      if (index === -1) this.queue[resource][key][type].push({ operation: 'file', data: file, processed: false });
    }
  }

  public queueReplace(resource: string, key: string, type: 'file' | 'image', file: MediaModel, config: FileConfigModel | ImageConfigModel) {
    this.createQueues(resource, key);
    //make sure we have the config for the file upload
    this.cache[resource][key][type].config = config;
    //if it's an existing file or we're currently uploading, queue it up
    //otherwise, it's a new file upload and will be in that queue
    if (file.mediaKey || file.uploading) {
      let found = this.queue[resource][key][type].find(item => (item.operation === 'replace' || item.operation === 'delete') && item.data == file);
      if (!found) this.queue[resource][key][type].push({ operation: 'replace', data: file, processed: false });
    }
  }

  /**
   * Queues up a reorder operation.
   * Will be executed whenever saveQueue/s() is called.
   */
  public queueReorder(resource: string, key: string, type: 'file' | 'image', files: MediaModel[]) {
    this.createQueues(resource, key);
    //in this case, just accept it from the component. Way easier to manage state there
    let index = this.queue[resource][key][type].findIndex(item => item.operation === 'reorder');
    if (index !== -1) this.queue[resource][key][type].splice(index, 1);
    this.queue[resource][key][type].push({ operation: 'reorder', data: files, processed: false });
  }

  /**
   * Queues up linking an image to a room.
   * Will be executed whenever saveQueue/s() is called
   */
  public queueLink(resource: string, key: string, file: MediaModel, room: any) {
    //TEMP: this is unused kinda sorta
    // this.createQueues(listingKey);
    // //queue it up
    // let index = this.queue[listingKey]['image'].findIndex(item => item.operation === 'link' && item.data.file === file);
    // if (index === -1) {
    //   this.queue[listingKey]['image'].push({ operation: 'link', data: { file: file, room: room }, processed: false });
    // } else {
    //   this.queue[listingKey]['image'].splice(index, 1, { operation: 'link', data: { file: file, room: room }, processed: false });
    // }
  }

  /**
   * Executes all file queues for a specific listing in the proper order
   */
  public async saveQueues(resource: string, key: string): Promise<{ processing: boolean, errors: string[], file: MediaUploadStatus, image: MediaUploadStatus }> {
    this.createQueues(resource, key);
    if (!this.status[resource][key].processing) { //only execute queue if not already
      this.status[resource][key].processing = true;
      this.status[resource][key].all$ = new Subject<void>();
      for (let type of [ 'file', 'image' ]) { //we doin it all at once now, screw it
        await this.saveQueue(resource, key, <any>type); //shut up compiler
      }
      this.status[resource][key].errors = [ ...this.status[resource][key].file.errors, ...this.status[resource][key].image.errors ];
      this.status[resource][key].processing = false;
      this.status[resource][key].all$.next();
      this.status[resource][key].all$.complete(); //must be completed when converting to promise
    } else { //already processing, wait for response
      await this.status[resource][key].all$.asObservable().toPromise();
    }
    return this.status[resource][key];
  }

  /**
   * Executes a specific listing's queue for either files or images
   */
  public async saveQueue(resource: string, key: string, type: 'file' | 'image'): Promise<MediaUploadStatus> {
    this.createQueues(resource, key);
    if (!this.status[resource][key][type].processing) { //only execute queue if not already
      this.status[resource][key][type].processing = true;
      this.status[resource][key][type + '$'] = new Subject<void>();
      if (this.queue[resource][key][type].length) {
        this.status[resource][key][type].errors = [];
        if (environment.confirmAction) window.onbeforeunload = function() { return 'Are you sure you want to leave? There are unfinished uploads remaining'; }
        let messageType: FileOperation =
          this.queue[resource][key][type].find(item => item.operation === 'restore') ? 'restore' :
            this.queue[resource][key][type].find(item => item.operation === 'delete') ? 'delete' :
              this.queue[resource][key][type].find(item => item.operation === 'model') ? 'model' :
                this.queue[resource][key][type].find(item => item.operation === 'file') ? 'file' :
                  this.queue[resource][key][type].find(item => item.operation === 'replace') ? 'replace' :
                    this.queue[resource][key][type].find(item => item.operation === 'reorder') ? 'reorder' :
                      this.queue[resource][key][type].find(item => item.operation === 'link') ? 'link' :
                        null;
        this.messageType$ = new BehaviorSubject<FileOperation>(messageType);
        let total = this.queue[resource][key][type].filter(item => item.operation === messageType).length;
        this.totalFiles$ = new BehaviorSubject<number>(total);
        this.currentFile$ = new BehaviorSubject<number>(1);
        //only bother showing the dialog if there's more than 1 in the queues
        //also if the dialog is already open don't reopen
        if ((!this.uploadDialog || this.uploadDialog?.getState() !== 0) && (
          this.queue[resource][key][type].filter(item => item.operation === 'restore').length > 1 ||
          this.queue[resource][key][type].filter(item => item.operation === 'delete').length > 1 ||
          this.queue[resource][key][type].filter(item => item.operation === 'model').length > 1 ||
          this.queue[resource][key][type].filter(item => item.operation === 'file').length > 1 ||
          this.queue[resource][key][type].filter(item => item.operation === 'replace').length > 1 ||
          this.queue[resource][key][type].filter(item => item.operation === 'link').length > 1
        )) {
          this.uploadDialog = this.dialog.open(UploadDialog, {
            ...UploadDialogConfig,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            data: {
              fileType: type,
              messageType$: this.messageType$,
              totalFiles$: this.totalFiles$,
              currentFile$: this.currentFile$
            }
          });
        }
      }
      //we want the changes to happen as fast as possible, so the queues are executed from fastest to slowest
      //the exception here is reorder, which must happen at the end to consolidate the backend's orderings with the frontend's
      //link is also just wanged on the end because it doesn't really matter
      for (let item of this.queue[resource][key][type]) {
        if (item.operation === 'link') {
          item.data.file.queued = true;
        } else if (item.operation !== 'reorder') {
          item.data.queued = true;
        }
      }
      this.fileChange$[resource][type].next();
      for (let operation of <FileOperation[]>[ 'restore', 'delete', 'model', 'file', 'replace', 'reorder', 'link' ]) {
        await this.processQueue(resource, key, type, operation);
      }

      //if no error and there are still queued operations, reprocess
      //because they might've added to the queue while the queue was processing
      if (!this.status[resource][key][type].errors.length && this.queue[resource][key][type].length) {
        this.status[resource][key][type].processing = false;
        return this.saveQueue(resource, key, type);
      }
      //cleanup
      this.uploadDialog && this.uploadDialog.getState() === 0 && this.uploadDialog.close(); //close if open
      this.totalFiles$ && this.totalFiles$.complete();
      this.currentFile$ && this.currentFile$.complete();
      this.uploadDialog = null;
      this.totalFiles$ = null;
      this.currentFile$ = null;
      window.onbeforeunload = null;
      this.status[resource][key][type].processing = false;
      this.status[resource][key][type + '$'].next();
      this.status[resource][key][type + '$'].complete(); //must be completed when converted to promise
    } else { //processing already, wait for the response
      await this.status[resource][key][type + '$'].asObservable().toPromise();
    }
    //return the status
    return this.status[resource][key][type];
  }

  private async processQueue(resource: string, key: string, type: 'file' | 'image', operation: FileOperation): Promise<void> {
    let items = this.queue[resource][key][type].filter(item => item.operation === operation);
    if (items.length) environment.log(type, operation, items);
    if (items.length) {
      if (operation !== 'reorder' && operation !== 'link')
        items.sort((a, b) => a.data.order === b.data.order ? 0 : a.data.order > b.data.order ? 1 : -1);
      this.messageType$ && this.messageType$.next(operation);
      this.totalFiles$ && this.totalFiles$.next(items.length);
      this.fileChange$[resource][type].next();
      for (let i = 0; i < items.length; i++) {
        this.currentFile$ && this.currentFile$.next(i + 1);
        await this.processQueueItem(resource, key, type, items[i]);
      }
      this.queue[resource][key][type] = this.queue[resource][key][type].filter(item => !item.processed);
      this.fileChange$[resource][type].next();
      //reprocess the remaining queue if there is one and there are no errors
      if (this.queue[resource][key][type].find(item => item.operation === operation) && !this.status[resource][key][type].errors.length)
        return this.processQueue(resource, key, type, operation);
    }
  }

  private async processQueueItem(resource: string, key: string, type: 'file' | 'image', item: FileQueueItem): Promise<void> {
    let promise = null;
    switch (item.operation) {
      case 'restore':
        promise = this.restoreFile(resource, key, type, item.data)
                      .catch(err => this.handleError(resource, key, type, item, err));
        break;
      case 'delete':
        promise = this.deleteFile(resource, key, type, item.data)
                      .catch(err => this.handleError(resource, key, type, item, err));
        break;
      case 'model':
        promise = this.uploadModel(resource, key, type, item.data)
                      .catch(err => this.handleError(resource, key, type, item, err));
        break;
      case 'file':
        promise = this.uploadFile(resource, key, type, item.data)
                      .then(() => {
                        if (item.data.uploaded && !item.data.error) item.data.localUrl = null;
                        if (type === 'image') for (let link of this.queue[resource][key][type].filter(file => file.operation === 'link')) {
                          if (link.data.file.mediaKey === item.data.mediaKey) {
                            if (link.data.room.MediaKeys?.length) {
                              if (!link.data.room.MediaKeys.includes(link.data.file.mediaKey)) {
                                link.data.room.MediaKeys = [ ...link.data.room.MediaKeys, link.data.file.mediaKey ];
                              }
                            } else {
                              link.data.room.MediaKeys = [ link.data.file.mediaKey ];
                            }
                          }
                        }
                        this.fileChange$[resource][type].next();
                      })
                      .catch(err => this.handleError(resource, key, type, item, err));
        break;
      case 'replace':
        this.fileChange$[resource][type].next();
        promise = this.replaceFile(resource, key, type, item.data)
                      .then(() => this.fileChange$[resource][type].next())
                      .catch(err => this.handleError(resource, key, type, item, err));
        break;
      case 'reorder':
        promise = this.reorderFiles(resource, key, type, item.data)
                      .catch(err => this.handleError(resource, key, type, item, err));
        break;
      default: break;
    }
    if (promise) {
      promise.finally(() => {
        if (!item.data.error) {
          item.processed = true;
          //clear image from cache
          if (type === 'image' && item.data?.file) {
            this.heicToService.clearFile(item.data.file.name);
            this.compressionService.clearFile(item.data.file.name);
          }
        }
      })
    }
    return promise;
  }

  private handleError(resource: string, key: string, type: 'file' | 'image', item: FileQueueItem, error: any) {
    if (item.operation === 'link') {
      item.data.file.retry = item.operation;
      item.data.file.error = error?.simple || error;
    } else if (item.operation !== 'reorder') {
      item.data.retry = item.operation;
      item.data.uploading = false;
      item.data.error = error?.simple || error;
    }
    this.status[resource][key][type].errors.push(item.data.originalMediaName + ' - ' +(error?.simple || error));
    this.fileChange$[resource][type].next();
  }
}

export interface FileQueueItem {
  operation: FileOperation,
  data: MediaModel | MediaModel[] | any,
  processed: boolean
}

export type FileOperation = 'restore' | 'delete' | 'model' | 'file' | 'replace' | 'reorder' | 'link';
